/**
 * lib/useAsyncData.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The one data-fetching hook. Every screen in this product loads with it, so
 * loading, failure, gating and refetch behave identically everywhere instead of
 * twelve times slightly differently.
 *
 * WHAT IT HANDLES, SO NO SCREEN HAS TO
 *  - try/catch → utils/errorHandler, so the backend's own message is what the
 *    user reads.
 *  - HTTP 412 → `gate`, not `error`. A configuration gate is not a failure and
 *    must render an amber bar, not a red toast; `handleApiError` already tones
 *    it correctly, and this exposes which gate so the screen can show the bar.
 *  - Staleness: it re-runs when `dataVersion` changes, which is how a write in
 *    a modal refreshes the table behind it.
 *  - Unmount safety: a response that lands after the screen is gone is dropped
 *    rather than setting state on a dead component.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { gateOf, handleApiError } from '../utils/errorHandler';
import type { GateKey } from './gate';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  /** The message already shown to the user, kept for inline rendering. */
  error: string | null;
  /** Set when the API answered 412 — render a gate bar, not an error. */
  gate: GateKey | null;
  /** Re-run the fetch. Safe to pass straight to a Retry button. */
  reload: () => void;
}

export interface UseAsyncDataOptions {
  /** Bumped by ShellContext on every write; a change re-runs the fetch. */
  version?: number;
  /** Toast title if it fails — "Could not load the register". */
  errorTitle: string;
  /** Skip the fetch (a dependency is not ready yet). */
  enabled?: boolean;
}

export function useAsyncData<T>(
  fetcher: () => Promise<T>,
  deps: readonly unknown[],
  { version = 0, errorTitle, enabled = true }: UseAsyncDataOptions,
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [gate, setGate] = useState<GateKey | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A manual counter, so `reload()` re-runs the effect without the caller
  // having to change any of its dependencies.
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  // The fetcher is a fresh closure on every render, so it cannot be an
  // effect dependency without looping. The caller's `deps` are the contract for
  // when it has meaningfully changed — the same arrangement as useCallback.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setGate(null);

    (async () => {
      try {
        const result = await fetcherRef.current();
        if (cancelled || !mounted.current) return;
        setData(result);
      } catch (caught) {
        if (cancelled || !mounted.current) return;
        const blocked = gateOf(caught);
        if (blocked) {
          // Not an error: the workspace has not been configured yet. The screen
          // renders the gate's amber bar and its resolving action.
          setGate(blocked);
          setData(null);
        } else {
          setError(handleApiError(caught, errorTitle));
        }
      } finally {
        if (!cancelled && mounted.current) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, version, attempt, enabled, errorTitle]);

  return { data, loading, error, gate, reload };
}
